import apiClient from '@/services/api';

interface AnalyticsConfig {
  apiUrl?: string;
  autoTrack?: boolean;
  debug?: boolean;
}

interface PageViewData {
  sessionId: string;
  userId?: string;
  pageUrl: string;
  pageTitle?: string;
  referrer?: string;
  deviceType?: string;
  browser?: string;
  os?: string;
  screenResolution?: string;
  language?: string;
  duration?: number;
}

interface EventData {
  sessionId: string;
  eventType: string;
  eventName: string;
  elementId?: string;
  elementText?: string;
  pageUrl?: string;
  metadata?: any;
}

class Analytics {
  private config: AnalyticsConfig = {
    apiUrl: '/api/analytics',
    autoTrack: true,
    debug: false,
  };
  
  private sessionId: string = '';
  private userId?: string;
  private pageStartTime: number = 0;
  private isInitialized: boolean = false;

  /**
   * 初始化埋点SDK
   */
  init(config: AnalyticsConfig = {}) {
    this.config = { ...this.config, ...config };
    this.sessionId = this.getOrCreateSessionId();
    this.isInitialized = true;

    if (this.config.debug) {
      console.log('[Analytics] Initialized with config:', this.config);
      console.log('[Analytics] Session ID:', this.sessionId);
    }

    // 自动追踪页面浏览
    if (this.config.autoTrack) {
      this.setupAutoTracking();
    }

    // 页面卸载时记录停留时间
    window.addEventListener('beforeunload', () => {
      this.trackPageDuration();
    });
  }

  /**
   * 设置用户ID
   */
  setUserId(userId: string) {
    this.userId = userId;
    if (this.config.debug) {
      console.log('[Analytics] User ID set:', userId);
    }
  }

  /**
   * 追踪页面浏览
   */
  trackPageView(pageUrl?: string, pageTitle?: string) {
    if (!this.isInitialized) {
      console.warn('[Analytics] Not initialized. Call init() first.');
      return;
    }

    this.pageStartTime = Date.now();

    const data: PageViewData = {
      sessionId: this.sessionId,
      userId: this.userId,
      pageUrl: pageUrl || window.location.pathname + window.location.search,
      pageTitle: pageTitle || document.title,
      referrer: document.referrer,
      deviceType: this.getDeviceType(),
      browser: this.getBrowser(),
      os: this.getOS(),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
    };

    this.sendPageView(data);

    if (this.config.debug) {
      console.log('[Analytics] Page view tracked:', data);
    }
  }

  /**
   * 追踪事件
   */
  trackEvent(eventType: string, eventName: string, metadata?: any) {
    if (!this.isInitialized) {
      console.warn('[Analytics] Not initialized. Call init() first.');
      return;
    }

    const data: EventData = {
      sessionId: this.sessionId,
      eventType,
      eventName,
      pageUrl: window.location.pathname + window.location.search,
      metadata,
    };

    this.sendEvent(data);

    if (this.config.debug) {
      console.log('[Analytics] Event tracked:', data);
    }
  }

  /**
   * 追踪点击事件
   */
  trackClick(elementId: string, elementText?: string, metadata?: any) {
    this.trackEvent('click', 'button_click', {
      elementId,
      elementText,
      ...metadata,
    });
  }

  /**
   * 追踪表单提交
   */
  trackFormSubmit(formName: string, metadata?: any) {
    this.trackEvent('form_submit', formName, metadata);
  }

  /**
   * 追踪文件下载
   */
  trackDownload(fileName: string, fileUrl: string) {
    this.trackEvent('download', 'file_download', {
      fileName,
      fileUrl,
    });
  }

  /**
   * 追踪搜索
   */
  trackSearch(keyword: string, results?: number) {
    this.trackEvent('search', 'search_query', {
      keyword,
      results,
    });
  }

  /**
   * 追踪视频播放
   */
  trackVideoPlay(videoTitle: string, videoUrl: string) {
    this.trackEvent('video_play', 'video_start', {
      videoTitle,
      videoUrl,
    });
  }

  /**
   * 追踪板块/子页面浏览
   */
  trackSectionView(section: string, subPage?: string) {
    this.trackEvent('section_view', section, {
      subPage,
      pageUrl: window.location.pathname,
    });
  }

  /**
   * 追踪导航点击
   */
  trackNavigation(from: string, to: string, label?: string) {
    this.trackEvent('navigation', 'nav_click', {
      from,
      to,
      label,
    });
  }

  /**
   * 追踪语言切换
   */
  trackLanguageSwitch(from: string, to: string) {
    this.trackEvent('interaction', 'language_switch', {
      from,
      to,
    });
  }

  /**
   * 追踪Tab/筛选切换
   */
  trackFilterChange(filterName: string, value: string) {
    this.trackEvent('interaction', 'filter_change', {
      filterName,
      value,
      pageUrl: window.location.pathname,
    });
  }

  /**
   * 设置自动追踪
   */
  private setupAutoTracking() {
    // 初始页面浏览
    this.trackPageView();

    // 监听路由变化（适用于SPA）
    let lastUrl = location.href;
    new MutationObserver(() => {
      const url = location.href;
      if (url !== lastUrl) {
        lastUrl = url;
        this.trackPageView();
      }
    }).observe(document, { subtree: true, childList: true });

    // 监听 popstate 事件（浏览器前进后退）
    window.addEventListener('popstate', () => {
      this.trackPageView();
    });
  }

  /**
   * 记录页面停留时间
   */
  private trackPageDuration() {
    if (this.pageStartTime > 0) {
      const duration = Math.floor((Date.now() - this.pageStartTime) / 1000);
      // 这里可以发送停留时间数据
      if (this.config.debug) {
        console.log('[Analytics] Page duration:', duration, 'seconds');
      }
    }
  }

  /**
   * 发送页面浏览数据
   */
  private async sendPageView(data: PageViewData) {
    try {
      await apiClient.post(`${this.config.apiUrl}/page-view`, data);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] Failed to send page view:', error);
      }
    }
  }

  /**
   * 发送事件数据
   */
  private async sendEvent(data: EventData) {
    try {
      await apiClient.post(`${this.config.apiUrl}/event`, data);
    } catch (error) {
      if (this.config.debug) {
        console.error('[Analytics] Failed to send event:', error);
      }
    }
  }

  /**
   * 获取或创建会话ID
   */
  private getOrCreateSessionId(): string {
    const key = 'analytics_session_id';
    let sessionId = sessionStorage.getItem(key);
    
    if (!sessionId) {
      sessionId = this.generateId();
      sessionStorage.setItem(key, sessionId);
    }
    
    return sessionId;
  }

  /**
   * 生成唯一ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 获取设备类型
   */
  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return '手机端';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return '手机端';
    }
    return '电脑';
  }

  /**
   * 获取浏览器信息
   */
  private getBrowser(): string {
    const ua = navigator.userAgent;
    if (ua.indexOf('Firefox') > -1) return 'Firefox';
    if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) return 'Opera';
    if (ua.indexOf('Trident') > -1) return 'IE';
    if (ua.indexOf('Edge') > -1) return 'Edge';
    if (ua.indexOf('Chrome') > -1) return 'Chrome';
    if (ua.indexOf('Safari') > -1) return 'Safari';
    return 'Unknown';
  }

  /**
   * 获取操作系统
   */
  private getOS(): string {
    const ua = navigator.userAgent;
    if (ua.indexOf('Win') > -1) return 'Windows';
    if (ua.indexOf('Mac') > -1) return 'MacOS';
    if (ua.indexOf('Linux') > -1) return 'Linux';
    if (ua.indexOf('Android') > -1) return 'Android';
    if (ua.indexOf('iOS') > -1) return 'iOS';
    return 'Unknown';
  }
}

// 导出单例
const analytics = new Analytics();
export default analytics;
